import './I9TermsOfService.scss';

import { Alert, Card, CardGroup, SkipNav } from '@ver-uds/uswds-react';
import { Button, ButtonGroup, Checkbox, PageHeader } from '@ver-uds/react';
import React, { UIEvent, useCallback, useRef, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useAuthState } from '@vis-auth/react';
import { useHistory } from 'react-router-dom';
import { EveRoutes } from '@ver-uds/e-verify';
import I9Constants from '../I9Constants/I9Constants';
import useAccountApi from '../../services/AccountApi';
import Divider from '../Divider/Divider';
import EVerifyPlusSuccessModal from '../EVerifyPlusSuccessModal/EVerifyPlusSuccessModal';
import I9TermsOfServiceContent from './I9TermsOfServiceContent';

const I9TermsOfService = ({ nawToAawToggle }: { nawToAawToggle: string }): JSX.Element => {
  const header = 'Terms of Service';
  const accountApi = useAccountApi();
  const { user } = useAuthState();
  const history = useHistory();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;
  const [eVerifyPlusSuccessModalIsOpen, setEVerifyPlusSuccessModalIsOpen] = useState<boolean>(false);
  const [termsOfUseAccepted, setTermsOfUseAccepted] = useState<boolean>(false); // Whether the user has checked the box
  const [scrolledThroughTOS, setScrolledThroughTOS] = useState<boolean>(false); // Whether the user has scrolled through the TOS
  const [isValid, setIsValid] = useState<boolean>(true); // Whether the submission data is valid
  const [isSending, setIsSending] = useState<boolean>(false); // Whether an API call is already in progess
  const [showAccountApiError, setShowAccountApiError] = useState<boolean>(false); // Whether an error occurred calling the API and an error is to be displayed
  const [evePlusEnabled, setEvePlusEnabled] = useState<boolean>(false); // Whether Eve+ was enabled already by another Program Admin
  const isMounted = useRef(true);

  const handleTosCheckboxChange = (): void => {
    if (!scrolledThroughTOS) {
      setIsValid(false);
    } else if (!termsOfUseAccepted) {
      setTermsOfUseAccepted(true);
      setIsValid(true);
    } else {
      setTermsOfUseAccepted(false);
    }
  };

  const handleEnableMyI9Clicked = useCallback(async () => {
    if (isSending) {
      return;
    }
    if (!termsOfUseAccepted) {
      setIsValid(false);
      return;
    }

    if (isValid) {
      setIsSending(true);
      setShowAccountApiError(false);

      try {
        await accountApi.updateMyI9({ accountId, myI9: { enabled: true } });
        setEVerifyPlusSuccessModalIsOpen(true);
      } catch (e: any) {
        if (e.status === 409) {
          setEvePlusEnabled(true);
          setEVerifyPlusSuccessModalIsOpen(true);
        } else {
          setShowAccountApiError(true);
        }
      } finally {
        if (isMounted.current) {
          // only update if we are still mounted
          setIsSending(false);
        }
      }
    }
  }, [isSending, isValid, termsOfUseAccepted, accountApi, accountId]);

  const handleCancelClick = (): void => {
    history.go(-1);
  };

  const redirectToTraining = (): void => {
    setEVerifyPlusSuccessModalIsOpen(false);
    window.location.href = EveRoutes.I9_TRAINING;
  };

  const handleScroll = (event: UIEvent): void => {
    if (!scrolledThroughTOS) {
      const target = event?.target as HTMLElement;
      const isZoomedOut = window.outerHeight / window.innerHeight < 0.5;
      const isAtBottom =
        (isZoomedOut && (target.scrollTop + target.clientHeight) / target.scrollHeight >= 0.999) ||
        (!isZoomedOut && Math.abs(target.scrollHeight - (target.scrollTop + target.clientHeight)) <= 1);
      if (isAtBottom) {
        setScrolledThroughTOS(true);
        setIsValid(true);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {I9Constants.PROGRAM_NAME} {header}
        </title>
      </Helmet>
      <PageHeader>{header}</PageHeader>
      <SkipNav.MainContent>
        <div className="app grid-container height-full">
          <div className="grid-row grid-gap">
            <main className="grid-row grid-col-12 padding-top-4 i9-terms-of-service-container">
              <div className="tablet:grid-col-9">
                <h1>Enable {I9Constants.PROGRAM_NAME}</h1>
                <p className="usa-intro">{I9Constants.I9_INTRO_TEXT}</p>
                {/* <p>
                  Learn more about what {I9Constants.PROGRAM_NAME} can do for you! Check out our{' '}
                  <b>{I9Constants.PROGRAM_NAME} information site</b>.
                </p> */}
                <Alert status="warning" className="margin-top-4">
                  <Alert.Heading>User Sessions will be Interrupted</Alert.Heading>
                  <Alert.Text>
                    Once {I9Constants.PROGRAM_NAME} has been enabled for your company account, all users must view and
                    certify completion of a training video before continuing to use E-Verify. Any users currently online
                    will be redirected from their active sessions to complete the training. All other users will be
                    prompted to complete their video training at their next sign-in.
                  </Alert.Text>
                </Alert>

                <CardGroup className="margin-top-4">
                  <Card className="tablet:grid-col-12">
                    <div className="outer-scroll-card-container">
                      <div
                        className="scroll-card-container"
                        onScroll={handleScroll}
                        data-testid="scroll-card-container"
                      >
                        <Card.Header as="h3">{I9Constants.PROGRAM_NAME} Terms of Service for Employers</Card.Header>
                        <Card.Body>{I9TermsOfServiceContent({ nawToAawToggle })}</Card.Body>
                      </div>
                    </div>
                  </Card>
                </CardGroup>

                <Checkbox
                  id="evePlusTermsAcceptedCheckbox"
                  data-testid="eveplus-terms-accepted-checkbox"
                  name="evePlusTermsAcceptedCheckbox"
                  className="bg-transparent"
                  label={`I agree to the ${I9Constants.PROGRAM_NAME} Terms of Service`}
                  checked={termsOfUseAccepted}
                  onChange={handleTosCheckboxChange}
                />
                <div>
                  {!isValid && (
                    <Alert status="error" className="margin-top-3">
                      <Alert.Text>
                        You must scroll down to review all conditions of the Terms of Service and agree by checking the
                        checkbox above to proceed.
                      </Alert.Text>
                    </Alert>
                  )}
                  {showAccountApiError && (
                    <Alert status="error" className="margin-top-3">
                      <Alert.Text>
                        Your account cannot be enabled at this time. Please wait a few minutes and try again.
                      </Alert.Text>
                    </Alert>
                  )}
                </div>
              </div>
              <div className="width-full">
                <Divider />
              </div>
              <div className="grid-col-12 margin-bottom-8">
                <ButtonGroup>
                  <Button variant="secondary" aria-label="Cancel" onClick={handleCancelClick}>
                    Cancel
                  </Button>
                  <Button
                    key={`Enable ${I9Constants.PROGRAM_NAME}`}
                    variant="submit"
                    disabled={isSending}
                    onClick={handleEnableMyI9Clicked}
                  >
                    Enable {I9Constants.PROGRAM_NAME}
                  </Button>
                </ButtonGroup>
              </div>
            </main>
          </div>
        </div>
        <EVerifyPlusSuccessModal
          isOpen={eVerifyPlusSuccessModalIsOpen}
          close={redirectToTraining}
          evePlusEnabled={evePlusEnabled}
        />
      </SkipNav.MainContent>
    </>
  );
};

export default I9TermsOfService;
